"use client";

import { useEffect } from "react";
import { useRouter, useParams } from "next/navigation";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Form } from "@/components/ui/form";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { TextField, SelectField, TextareaField, SwitchField, DateField, FileUploadField, S3UploadField, DynamicSelectField } from "@admin/_components/form-fields";
import { useApiPost } from "@admin/_lib/api-hooks";
import { useQuery } from "@tanstack/react-query";
import { useSession } from "next-auth/react";

export interface FormFieldConfig {
  key: string;
  label: string;
  type: "text" | "email" | "password" | "number" | "url" | "textarea" | "select" | "switch" | "date" | "file" | "s3-file";
  required?: boolean;
  options?: { value: string; label: string }[];
  placeholder?: string;
  description?: string;
  disabled?: boolean;
  colSpan?: 1 | 2;
  /** Accepted MIME types for file fields, e.g. "image/*" */
  accept?: string;
  /** S3 bucket for file uploads */
  bucket?: string;
  /** S3 folder path (prefix) for file uploads, e.g. "divine-infinity-being/tarot-spread-image/" */
  path?: string;
  /**
   * When set, the field renders as a dynamic select whose options are fetched
   * from this API endpoint at runtime (POST). Requires type: "select".
   */
  dynamicOptionsEndpoint?: string;
  /** Key in each item to use as option value. Default: "_id" */
  dynamicOptionsValueKey?: string;
  /** Key in each item to use as option label. Default: "title" */
  dynamicOptionsLabelKey?: string;
  /** Optional POST body for the dynamic options request */
  dynamicOptionsBody?: Record<string, unknown>;
  /** Whether this is a multi-select field */
  isMulti?: boolean;
}

export interface GenericFormConfig {
  title: string;
  addTitle?: string;
  editTitle?: string;
  subtitle?: string;
  fetchEndpoint?: string;
  addEndpoint: string;
  editEndpoint: string;
  backPath: string;
  successPath?: string;
  fields: FormFieldConfig[];
  /** Query keys to invalidate after success (e.g. the list endpoint) */
  invalidateKeys?: string[];
}

type FormValues = Record<string, any>;

function buildSchema(fields: FormFieldConfig[]) {
  const shape: Record<string, z.ZodTypeAny> = {};
  for (const field of fields) {
    if (field.type === "switch") {
      shape[field.key] = z.boolean().default(true);
    } else if (field.type === "s3-file" || field.isMulti) {
      shape[field.key] = z.array(z.any()).default([]);
    } else if (field.type === "email") {
      const base = z.string().email("Invalid email address");
      shape[field.key] = field.required ? base : z.union([base, z.literal("")]).default("");
    } else if (field.type === "number") {
      shape[field.key] = z.any(); // Handled in onSubmit conversion
    } else {
      const base = z.string();
      shape[field.key] = field.required
        ? base.min(1, `${field.label} is required`)
        : base.optional().default("");
    }
  }
  return z.object(shape);
}

function getDefaultValues(fields: FormFieldConfig[]): FormValues {
  return Object.fromEntries(
    fields.map((f) => [
      f.key,
      f.type === "switch" ? true : f.type === "s3-file" || f.isMulti ? [] : "",
    ])
  );
}

export function GenericFormPage({ config }: { config: GenericFormConfig }) {
  const router = useRouter();
  const params = useParams();
  const id = params?.id as string | undefined;
  const isEdit = !!id;
  const { data: session } = useSession();

  const schema = buildSchema(config.fields);

  const { data: record, isLoading: isFetching } = useQuery({
    queryKey: [config.fetchEndpoint, id],
    queryFn: async () => {
      if (!config.fetchEndpoint) return null;
      const baseUrl =
        process.env.NEXT_PUBLIC_API_CLOUDFRONT ||
        "https://api.divineinfinitebeing.com/";
      const res = await fetch(`${baseUrl}${config.fetchEndpoint}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.token}`,
        },
        body: JSON.stringify({ _id: id }),
      });
      const data = await res.json();
      const result = data.results?.res ?? data.results ?? data.res ?? data;
      return Array.isArray(result) ? result[0] : result;
    },
    enabled: isEdit && !!session?.token && !!config.fetchEndpoint,
  });

  const mutation = useApiPost(isEdit ? config.editEndpoint : config.addEndpoint, {
    invalidateKeys: [
      ...(config.invalidateKeys || []),
      ...(config.fetchEndpoint ? [config.fetchEndpoint] : []),
    ],
  });

  const form = useForm<FormValues>({
    resolver: zodResolver(schema) as any,
    defaultValues: getDefaultValues(config.fields),
  });

  useEffect(() => {
    if (record) {
      const values: FormValues = {};
      for (const field of config.fields) {
        if (field.type === "switch") {
          values[field.key] = record[field.key] === 1 || record[field.key] === true;
        } else if (field.type === "file" || field.type === "s3-file") {
          const val = record[field.key];
          if (field.type === "s3-file") {
            values[field.key] = Array.isArray(val) ? val : val ? [val] : [];
          } else if (Array.isArray(val) && val[0]) {
            values[field.key] = val[0].imgUrl || val[0].url || "";
          } else if (typeof val === "object" && val !== null) {
            values[field.key] = (val as any).imgUrl || (val as any).url || "";
          } else {
            values[field.key] = val != null ? String(val) : "";
          }
        } else if (field.type === "select") {
          const val = record[field.key];
          if (field.isMulti) {
            values[field.key] = Array.isArray(val) ? val.map(String) : [];
          } else {
            values[field.key] = val != null ? String(val) : "";
          }
        } else {
          values[field.key] = record[field.key] != null ? String(record[field.key]) : "";
        }
      }
      form.reset(values);
    }
  }, [record, config.fields, form]);

  async function onSubmit(values: FormValues) {
    try {
      const body: Record<string, unknown> = { ...values };
      for (const field of config.fields) {
        if (field.type === "switch") {
          body[field.key] = values[field.key] ? 1 : 0;
        } else if (field.type === "number" && typeof values[field.key] === "string") {
          const num = parseFloat(values[field.key]);
          if (!isNaN(num)) body[field.key] = num;
        } else if (field.type === "s3-file") {
          // Send the full object array for S3 files
          body[field.key] = values[field.key];
        }
      }
      if (isEdit) body._id = id;

      await mutation.mutateAsync(body as any);
      toast.success(isEdit ? "Updated successfully" : "Created successfully");
      router.push(config.successPath ?? config.backPath);
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Operation failed");
    }
  }

  if (isEdit && isFetching) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="size-9 rounded-md" />
          <Skeleton className="h-8 w-48" />
        </div>
        <Skeleton className="h-96 rounded-xl" />
      </div>
    );
  }

  const title = isEdit
    ? (config.editTitle ?? `Edit ${config.title}`)
    : (config.addTitle ?? `Add ${config.title}`);

  return (
    <div className="space-y-6">
      {/* ── Page title with orange accent ── */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" className="shrink-0" onClick={() => router.back()}>
          <ArrowLeft className="size-4" />
        </Button>
        <div className="h-6 w-1 rounded-full shrink-0" style={{ background: "linear-gradient(to bottom, #ffa800, #ff7e00)" }} />
        <div>
          <h1 className="text-xl font-semibold text-foreground">{title}</h1>
          {config.subtitle && (
            <p className="text-xs text-muted-foreground mt-0.5">{config.subtitle}</p>
          )}
        </div>
      </div>

      <Card>
        <CardHeader className="border-b" style={{ borderColor: "hsl(240 20% 20%)" }}>
          <CardTitle className="flex items-center gap-2 text-base">
            <span className="h-4 w-0.5 rounded-full" style={{ background: "#ffa800" }} />
            {config.title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <div className="grid gap-4 md:grid-cols-2">
                {config.fields.map((field) => {
                  const isFullWidth =
                    field.colSpan === 2 ||
                    field.type === "textarea" ||
                    field.type === "switch" ||
                    field.type === "file";

                  const commonProps = {
                    control: form.control as any,
                    name: field.key,
                    label: field.label,
                    placeholder: field.placeholder,
                    description: field.description,
                    disabled: field.disabled,
                  };

                  const wrapClass = isFullWidth ? "md:col-span-2" : "";

                  if (field.type === "textarea") {
                    return (
                      <div key={field.key} className={wrapClass}>
                        <TextareaField {...commonProps} />
                      </div>
                    );
                  }

                  if (field.type === "switch") {
                    return (
                      <div key={field.key} className={wrapClass}>
                        <SwitchField {...commonProps} />
                      </div>
                    );
                  }

                  if (field.type === "select") {
                    if (field.dynamicOptionsEndpoint) {
                      return (
                        <div key={field.key} className={wrapClass}>
                          <DynamicSelectField
                            {...commonProps}
                            endpoint={field.dynamicOptionsEndpoint}
                            valueKey={field.dynamicOptionsValueKey}
                            labelKey={field.dynamicOptionsLabelKey}
                            body={field.dynamicOptionsBody}
                            isMulti={field.isMulti}
                          />
                        </div>
                      );
                    }
                    return (
                      <div key={field.key} className={wrapClass}>
                        <SelectField {...commonProps} options={field.options ?? []} />
                      </div>
                    );
                  }

                  if (field.type === "date") {
                    return (
                      <div key={field.key} className={wrapClass}>
                        <DateField {...commonProps} />
                      </div>
                    );
                  }

                  if (field.type === "file") {
                    return (
                      <div key={field.key} className={wrapClass}>
                        <FileUploadField {...commonProps} accept={field.accept} />
                      </div>
                    );
                  }

                  if (field.type === "s3-file") {
                    return (
                      <div key={field.key} className={wrapClass}>
                        <S3UploadField
                          {...commonProps}
                          bucket={field.bucket || "all-frontend-assets"}
                          path={field.path || ""}
                          accept={field.accept}
                        />
                      </div>
                    );
                  }

                  return (
                    <div key={field.key} className={wrapClass}>
                      <TextField
                        {...commonProps}
                        type={
                          field.type === "password"
                            ? "password"
                            : field.type === "email"
                              ? "email"
                              : field.type === "number"
                                ? "number"
                                : field.type === "url"
                                  ? "url"
                                  : "text"
                        }
                      />
                    </div>
                  );
                })}
              </div>

              <div className="flex gap-3 pt-2">
                {/* Orange gradient submit — matches Angular --orange-gradient CTA buttons */}
                <button
                  type="submit"
                  disabled={mutation.isPending}
                  className="inline-flex items-center gap-2 rounded-md px-5 py-2 text-sm font-semibold transition-all disabled:opacity-60 disabled:cursor-not-allowed btn-orange"
                >
                  {mutation.isPending ? "Saving..." : isEdit ? "Update" : "Create"}
                </button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => router.back()}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
